import type { BaseOptionType } from "antd/lib/select"

import {
  REPORT_FILTER_DIMENSIONS,
  type ReportDefinitionV1,
  type ReportFilterDimension,
  isCustomWindow
} from "../types/canvas.types.ts"

export const FILTER_DIMENSION_LABEL_KEYS: Record<
  ReportFilterDimension,
  string
> = {
  project: "reports.filters.project",
  pm: "reports.filters.pm",
  client: "reports.filters.client",
  project_type: "reports.filters.projectType",
  project_status: "reports.filters.projectStatus",
  status: "reports.filters.status",
  state: "reports.filters.state"
}

export interface ReportFilterSummaryRow {
  key: "window" | "compare" | ReportFilterDimension
  labelKey: string
  /** The applied values, by name where the option list knows them. */
  values: string[]
}

type Translate = (key: string, options?: Record<string, unknown>) => string

/** The default date range a report opens on when it never chose one. */
const DEFAULT_WINDOW_KEY = "reports.window.this_month"

/**
 * What a report's saved defaults apply, row by row, for the Filters step's
 * summary, the builder's read-only line and Review & share. Only filters that
 * narrow anything are listed; the date range is always listed because every
 * widget is resolved against one.
 */
export const summarizeReportFilters = (
  definition: ReportDefinitionV1 | null | undefined,
  options: Partial<Record<ReportFilterDimension, BaseOptionType[]>>,
  t: Translate
): ReportFilterSummaryRow[] => {
  const window = definition?.window
  const rows: ReportFilterSummaryRow[] = [
    {
      key: "window",
      labelKey: "reports.window.label",
      values: [
        !window
          ? t(DEFAULT_WINDOW_KEY)
          : isCustomWindow(window)
            ? `${window.from} – ${window.to}`
            : t(`reports.window.${window.preset}`)
      ]
    }
  ]
  if (definition?.compare)
    rows.push({
      key: "compare",
      labelKey: "reports.compare.label",
      values: [
        definition.compare === "previous_period"
          ? t("reports.compare.previousPeriod")
          : t("reports.compare.samePeriodLastYear")
      ]
    })

  for (const dimension of REPORT_FILTER_DIMENSIONS) {
    const selected = definition?.filters?.[dimension] ?? []
    if (selected.length === 0) continue
    const labels = new Map(
      (options[dimension] ?? []).map((option) => [
        String(option.value),
        String(option.label ?? option.value)
      ])
    )
    rows.push({
      key: dimension,
      labelKey: FILTER_DIMENSION_LABEL_KEYS[dimension],
      values: selected.map(
        (value) => labels.get(String(value)) ?? String(value)
      )
    })
  }
  return rows
}
